import { Injectable, OnDestroy, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { SocketService } from './socket.service';
import { QzService } from '../qz.service';
import { PrintNodeService } from '../print-node.service';
import { ToastService } from '../toast.service';
import { environment } from '@env/environment';
import { EVENT_GATEWAY } from '@core/enums/eventWebSocket.enum';
import { IEventNotification } from '@core/interfaces/socket.interface';
import { PrintJobEvent } from '@core/interfaces/request-response/impresora.dto.interface';
import { EmpresaService } from '../empresa.service';
import {
  buildImpresion,
  obtenerFooterMensajePorDocumento,
} from '@shared/utils/formatos_impresion.utils';
import {
  buildPromocionesHtml,
  extractPromocionesImpresion,
} from '@shared/utils/formatos_promocion_impresion.utils';
import { IConfigEmpresaRes } from '@core/interfaces/request-response/empresa.dto.interface';
import { logger } from '@shared/utils/functions.utils';
import { ETipoDocumentoSUNAT } from '@core/enums/tipoDocumentoSUNAT.enum';
import { SucursalService } from '../sucursal.service';

@Injectable({
  providedIn: 'root',
})
export class PrintJobListenerService implements OnDestroy {
  /* ===================== INYECCIONES ===================== */

  private readonly socketService = inject(SocketService);
  private readonly qz = inject(QzService);
  private readonly printNode = inject(PrintNodeService);
  private readonly toastService = inject(ToastService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly empresaService = inject(EmpresaService);
  private readonly sucursalService = inject(SucursalService);

  /* ===================== ESTADO ===================== */

  private readonly namespace = '/impresora';

  private initialized = false;
  private currentSucursalId: number | null = null;

  private empresaConfigData: IConfigEmpresaRes | null = null;

  // 🔥 Cola real FIFO
  private printQueue: PrintJobEvent[] = [];
  private isProcessing = false;

  private readonly destroy$ = new Subject<void>();

  /* ===================== OBSERVABLES ===================== */

  private readonly eventNotificationSubject = new Subject<IEventNotification<PrintJobEvent>>();

  readonly events$ = this.eventNotificationSubject.asObservable();

  constructor() {
    this.empresaService.empresaConfigData$.pipe(takeUntil(this.destroy$)).subscribe((empresa) => {
      this.empresaConfigData = empresa;
    });
  }

  /* =========================================================
     START
  ========================================================= */

  async start(sucursalId: number): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!environment.qzTrayEnabled) return;
    if (this.initialized && this.currentSucursalId === sucursalId) return;

    try {
      // Limpiar listeners anteriores si hay
      if (this.initialized && this.currentSucursalId !== sucursalId) {
        this.stop();
      }

      const qzReady = await this.qz
        .init()
        .then(() => true)
        .catch(() => false);
      if (!qzReady) return;

      if (!this.printNode.isConfigured(sucursalId)) return;

      const socket = this.socketService.conectarSucursal(sucursalId, this.namespace);
      logger('Conectando a socket para impresión, sucursal:', sucursalId, 'Socket:', socket);

      if (!socket) return;
      logger('Socket conectado para impresión:', socket);

      this.currentSucursalId = sucursalId;
      logger('Inicializando listeners de impresión para sucursal:', sucursalId);
      this.initializeListeners(socket, sucursalId);
      this.initialized = true;
    } catch (err) {
      console.error('[PrintJobListener] Init error:', err);
    }
  }

  /* =========================================================
     SOCKET LISTENERS
  ========================================================= */

  private initializeListeners(socket: any, sucursalId: number) {
    logger('Obteniendo configuración local de impresión para sucursal:', sucursalId);
    const localConfig = this.printNode.getConfigBySucursal(sucursalId);
    logger('Configuración local para impresión:', localConfig);
    if (!localConfig) return;

    // Limpiar listeners anteriores antes de registrar nuevos
    this.limpiarListeners(socket);

    socket.on(EVENT_GATEWAY.CONNECT, () => {
      if (!environment.production) {
        logger('🟢 WebSocket conectado');
      }
      logger('Conectado al socket de impresión para sucursal:', sucursalId);
      this.processQueue();
    });

    socket.on(EVENT_GATEWAY.DISCONNECT, (reason: string) => {
      logger('🔴 WebSocket desconectado:', reason);
      console.warn('🔴 WebSocket desconectado:', reason);
    });

    socket.on(EVENT_GATEWAY.PRINT_JOB, (job: PrintJobEvent) => {
      logger('📩 Print job recibido:', job);
      logger('Comparando con config local:', localConfig);
      if (job.config_impresora_id !== localConfig.configImpresoraId) return;

      if (localConfig.sucursalId && job.sucursal_id && job.sucursal_id !== localConfig.sucursalId)
        return;

      this.enqueueJob(job);
    });
  }

  /**
   * Limpia listeners previos para evitar duplicados si start() se llama más de una vez
   */
  private limpiarListeners(socket: any) {
    socket.off(EVENT_GATEWAY.CONNECT);
    socket.off(EVENT_GATEWAY.DISCONNECT);
    socket.off(EVENT_GATEWAY.PRINT_JOB);
  }

  /* =========================================================
     COLA DE IMPRESIÓN (FIFO)
  ========================================================= */

  private enqueueJob(job: PrintJobEvent) {
    this.printQueue.push(job);
    this.processQueue();
  }

  private async processQueue() {
    if (this.isProcessing) return;
    if (this.printQueue.length === 0) return;

    this.isProcessing = true;

    while (this.printQueue.length > 0) {
      const job = this.printQueue.shift()!;
      await this.safeProcessJob(job);
    }

    this.isProcessing = false;
  }

  /* =========================================================
     PROCESAMIENTO SEGURO CON RETRY
  ========================================================= */

  private async safeProcessJob(job: PrintJobEvent) {
    const jobId = job.print_job_id || `job-${Date.now()}`;

    try {
      if (!this.empresaConfigData) {
        throw new Error('Empresa no disponible');
      }

      const localConfig = this.printNode.getConfigBySucursal(job.sucursal_id);
      if (!localConfig) {
        throw new Error('Config impresora no encontrada');
      }

      // buildImpresion es ahora async - carga templates HTML
      const jobData = job.data as Record<string, any>;
      const tipoDocumento = jobData?.['documento_sunat']?.['tipo_documento_sunat'] as
        | ETipoDocumentoSUNAT
        | undefined;
      const sucursal = this.sucursalService.sucursalPorID(job.sucursal_id) ?? null;
      const footerMensaje = tipoDocumento
        ? obtenerFooterMensajePorDocumento(sucursal, tipoDocumento)
        : undefined;

      const htmlContent = await buildImpresion(
        this.empresaConfigData,
        job.tipo_impresion,
        job.data,
        footerMensaje,
      );
      logger('Enviando a impresión HTML:', { jobId, printer: localConfig.printerName });

      if (this.qz.isVirtualPdfPrinter(localConfig.printerName)) {
        await this.qz.printHtmlWithBrowserDialog(htmlContent, `VentaClick ${jobId}`);
      } else {
        await this.qz.printData(localConfig.printerName, htmlContent, {
          cut: true,
          feedLines: 1,
          format: 'html',
          paperWidth: localConfig.widthMm,
        });
      }

      const promociones = extractPromocionesImpresion(jobData);
      if (promociones.length > 0) {
        const promocionesHtml = buildPromocionesHtml(promociones);

        if (this.qz.isVirtualPdfPrinter(localConfig.printerName)) {
          await this.qz.printHtmlWithBrowserDialog(
            promocionesHtml,
            `VentaClick Promociones ${jobId}`,
          );
        } else {
          await this.qz.printData(localConfig.printerName, promocionesHtml, {
            cut: true,
            feedLines: 1,
            format: 'html',
            paperWidth: localConfig.widthMm,
          });
        }
      }

      this.toastService.show(
        'top-right',
        'success',
        'Impresión enviada',
        `Documento #${jobId} impreso`,
      );
    } catch (error) {
      console.error('Error impresión:', error);

      // 🔥 Retry automático (1 intento adicional)
      setTimeout(() => this.enqueueJob(job), 3000);

      this.toastService.show(
        'top-right',
        'error',
        'Error impresión',
        `Reintentando documento #${jobId}`,
      );
    }
  }

  /* =========================================================
     UTILIDADES
  ========================================================= */

  stop(): void {
    if (!this.currentSucursalId) return;

    const socket = this.socketService.obtenerSocket(this.currentSucursalId, this.namespace);

    if (socket) {
      this.limpiarListeners(socket);
    }

    // Desconecta solo el socket de impresora, no afecta otros namespaces
    this.socketService.desconectarSocketEspecifico(this.currentSucursalId, this.namespace);

    this.initialized = false;
    this.currentSucursalId = null;
    this.printQueue = [];
  }

  ngOnDestroy(): void {
    this.stop();
    this.destroy$.next();
    this.destroy$.complete();
    this.eventNotificationSubject.complete();
  }
}
